/**
 * Converts a Date or date string into an ISO string adjusted for the user's local timezone.
 * 
 * @param {Date | string} dateInput - A Date object or a parseable date string.
 * @returns {string} ISO string adjusted for timezone offset.
 * 
 * Example: toIsoWithTimezoneOffset('2025-07-31T12:00') 
 * might return '2025-07-31T12:00:00.000Z' depending on the local offset.
 */
export function toIsoWithTimezoneOffset(dateInput) {
  const date = new Date(dateInput);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString();
}

/**
 * Returns the current local datetime in ISO format truncated to minutes (YYYY-MM-DDTHH:mm).
 * 
 * @returns {string} ISO local datetime string, sliced to minutes.
 * 
 * Example: "2025-07-31T09:15"
 */
export function nowLocalIso() {
  return toIsoWithTimezoneOffset(new Date()).slice(0, 16);
}

export function nowUtcIsoWithTime() {
  return toUtcIsoWithTime(new Date()).slice(0, 16);
}

export function toLocalInputFormat(isoString, withTime = true) {
  if (!isoString) return '';
  const date = new Date(isoString);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return withTime ? `${yyyy}-${mm}-${dd}T${hh}:${min}` : `${yyyy}-${mm}-${dd}`;
};

/**
 * Parses a date string that may be missing timezone information and returns a
 * Date object interpreted as UTC.
 *
 * @param {Date|string} input
 * @returns {Date}
 */
export function parseUtc(input) {
  if (!input) return new Date(NaN);
  if (input instanceof Date) return input;
  let str = String(input).replace(' ', 'T');
  if (!/Z$|[+\-]\d{2}:\d{2}$/.test(str)) {
    str += 'Z';
  }
  return new Date(str);
}

/**
 * Parses a date string in DD/MM/YYYY format and returns an ISO date string (YYYY-MM-DD).
 *
 * @param {string} dateStr - A date in DD/MM/YYYY format.
 * @returns {string | null} ISO date string or null if invalid.
 * 
 * Example: parseDateFromDDMMYYYY("31/12/2025") → "2025-12-31"
 */
export function parseDateFromDDMMYYYY(dateStr) {
  if (typeof dateStr !== 'string') return null;
  const [day, month, year] = dateStr.split('/');
  if (!day || !month || !year) return null;

  const date = new Date(`${year}-${month}-${day}`);
  if (
    date.getFullYear() !== Number(year) ||
    date.getMonth() + 1 !== Number(month) ||
    date.getDate() !== Number(day)
  ) {
    return null; // invalid date
  }

  return date.toISOString().slice(0, 10); // "YYYY-MM-DD"
}

/**
 * Parses a date string in YYYY/MM/DD format and returns an international date format (DD-MMM-YY).
 * 
 * @param {string} d - A date in DD/MM/YYYY format.
 * @returns {string | null} - A date in DD-MMM-YY format
 * 
 * Example: formatScheduledDate("2025-12-31") → "21-Dec-2025"
 */
export function formatIntDate(d) {
  if (!d) return '';
  const date = parseUtc(d);
  const dd = String(date.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const mmm = monthNames[date.getMonth()];
  const yy = String(date.getFullYear()).slice(-2);
  return `${dd}-${mmm}-${yy}`;
};

export function formatIntDateTime(d) {
  if (!d) return '';
  const date = parseUtc(d);

  // Date parts
  const dd = String(date.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const mmm = monthNames[date.getMonth()];
  const yy = String(date.getFullYear()).slice(-2);

  // Time parts
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');

  return `${dd}-${mmm}-${yy} ${hh}:${min}`;
}

// Normalize dates to UTC ISO

export function toUtcIso(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10);
};

export function toUtcIsoWithTime(localString) {
  if (!localString) return null;
  const parsed = new Date(localString);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

// 2-digit date and datetime formats

export function formatDate(d) {
  if (!d) return '—';
  const date = parseUtc(d);
  const datePart = date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
  return `${datePart}`;
}

export function formatDateTime(d) {
  if (!d) return '—';
  const date = parseUtc(d);
  const datePart = date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
  const timePart = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${datePart} ${timePart}`;
}

/* Formats a UTC date YYYY-MM-DD HH:mm:ssZ as a Date Only DD/MM/YYYY */

export function formatDateOnly(d) {
  if (!d) return '—';
  const date = parseUtc(d);
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}
